import { useState, useEffect } from "react";
import SEO from "../seo";
import {
  PLANS_PRICING,
  STAFF_ACCOUNTS,
  TRANSACTION_FEES,
} from "../constant_data";

const GrowthPlan = () => {
  const [activeSection, setActiveSection] = useState("pricing");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["pricing", "features", "perfect-for"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <SEO
        title="xPage Growth Plan – Scale Your Business with xPage"
        description="Scale your business with xPage Growth Plan. Enhanced automation, advanced analytics, reduced transaction fees, and team collaboration tools for growing stores."
        keywords="xPage Growth Plan, eCommerce growth plan, scaling business, team collaboration, advanced analytics"
        canonical="https://help.xpage.ai/help/pricing-plans/Growth%20Plan"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold">
            Growth Plan
          </h1>
          <p className="text-sm text-systemSubTitle mt-1">
            The Growth Plan is built for growing businesses ready to scale their
            operations and boost conversion rates. It's ideal for brands that
            have validated their product-market fit and need more flexibility,
            better transaction rates, and expanded team collaboration.
          </p>
        </div>

        {/* Pricing Details */}
        <div id="pricing" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Pricing
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-tag text-xl text-core mb-2"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-1">
                ${PLANS_PRICING.GROWTH.MONTHLY}
              </h3>
              <p className="text-systemLabel text-sm">per month</p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-credit-card text-xl text-core mb-2"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-1">
                Card Processing
              </h3>
              <p className="text-systemLabel text-sm">
                {TRANSACTION_FEES.GROWTH.CARD}% fee
              </p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-money-bill-wave text-xl text-core mb-2"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-1">
                COD Processing
              </h3>
              <p className="text-systemLabel text-sm">
                {TRANSACTION_FEES.GROWTH.COD}% fee
              </p>
            </div>
          </div>
        </div>

        {/* Features Table */}
        <div id="features" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Everything Included
          </h2>
          <div className="bg-systemBackground border border-systemBorder rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-400/10 border-b border-systemBorder">
                  <th className="text-left p-3 text-systemTitle text-sm font-semibold">
                    Feature Category
                  </th>
                  <th className="text-left p-3 text-systemTitle text-sm font-semibold">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Core Features
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Everything in the Starter Plan, plus enhanced automation
                    tools, advanced layout customization, and performance
                    optimization for high-traffic pages.
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Staff Accounts
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    {STAFF_ACCOUNTS.GROWTH} staff member accounts for seamless
                    collaboration and operational scalability.
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Payment & Checkout
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Reduced transaction fees with {TRANSACTION_FEES.GROWTH.CARD}
                    % card rate and {TRANSACTION_FEES.GROWTH.COD}% COD rate.
                    Access to multiple gateways (Stripe, Paypal, Bancontact, COD
                    & more) with a conversion-optimized checkout experience.
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Analytics
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Comprehensive performance dashboard with advanced traffic
                    insights, sales funnel visualization, and conversion
                    metrics.
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Marketing Tools
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Enhanced automation suite: email marketing integrations,
                    upsell & cross-sell modules, personalized discounts, and
                    referral program management.
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    International Selling
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Multi-currency & multi-language support, international
                    shipping and duties management.
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Hosting & Security
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Unlimited hosting, free SSL, and enterprise-grade
                    PCI-compliant payment security.
                  </td>
                </tr>
                <tr className="hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Threshold System
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Dynamic sales-based billing that adapts as your business
                    scales beyond the $15 threshold, maintaining predictable
                    growth-friendly costs.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Who It's For Section */}
        <div id="perfect-for" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Perfect For
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-chart-line text-2xl text-core mb-3"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-2">
                Established Sellers
              </h3>
              <p className="text-systemLabel text-sm">
                Expanding into new markets or increasing product lines.
              </p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-users text-2xl text-core mb-3"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-2">
                Growing Teams
              </h3>
              <p className="text-systemLabel text-sm">
                That need multi-user access and collaboration tools.
              </p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-bullseye text-2xl text-core mb-3"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-2">
                Optimizing Businesses
              </h3>
              <p className="text-systemLabel text-sm">
                Optimizing checkout conversion and transaction fees to maximize
                profit margins.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#pricing"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "pricing"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Pricing
              </a>
            </li>
            <li>
              <a
                href="#features"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "features"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Everything Included
              </a>
            </li>
            <li>
              <a
                href="#perfect-for"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "perfect-for"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Perfect For
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default GrowthPlan;
